import React, {useCallback, useRef, useState} from 'react';

import {AppRoute} from '../../../models/AppRoute';
import {AccountScreenAction, RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import apiService from '../../../transport/apiService';
import {Analytics} from '../../../utils/analytics/analytics';
import {EventName, EventScreenName, EventSourceType} from '../../../utils/analytics/events';
import {logError} from '../../../utils/errorLogging';

import SignUpFlowContent, {SignUpEmailOutcome} from './SignUpFlowContent';

const INVALID_EMAIL_MESSAGE = 'Please enter a valid email';
const LOOKUP_FAILED_MESSAGE = 'An error occurred. Please try again.';
const WRONG_PASSWORD_MESSAGE = 'That password is incorrect.';

/**
 * Routed container for the sign-up entry flow.
 *
 * SignUpFlowContent is presentational: it owns the two steps and the
 * transition, and takes the account lookup, the login and every exit as
 * props. This screen is the only place those are bound to the user store and
 * the navigator, so the flow stays renderable in Storybook.
 */
const SignUpFlowScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_NOT_LOGGED_IN>) => {
  const {userStore} = useStores();

  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | undefined>();
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | undefined>();

  // checkEmail2 returns the member's first name alongside the status, and the
  // rewards journey wants it. Held here so the outcome union stays a plain
  // status and does not have to carry a payload.
  const firstName = useRef<string | undefined>(undefined);

  const onCheckEmail = useCallback(
    async (email: string): Promise<SignUpEmailOutcome> => {
      firstName.current = undefined;
      setEmailErrorMessage(undefined);

      try {
        const validation = await apiService.validateEmail(email);
        if (!validation.data.valid) {
          setEmailErrorMessage(INVALID_EMAIL_MESSAGE);
          return 'unavailable';
        }

        const data = await userStore.checkEmailNew(email);

        switch (data?.status) {
          case 'ACCOUNT_LOGIN':
            return 'existing';
          case 'REWARD_FULL_SIGNUP':
            firstName.current = data.firstName;
            return 'rewardsSignUp';
          case 'ACCOUNT_CREATE':
            return 'new';
          default:
            setEmailErrorMessage(LOOKUP_FAILED_MESSAGE);
            return 'unavailable';
        }
      } catch (error) {
        logError(error, 'SignUpFlowScreen.onCheckEmail');
        setEmailErrorMessage(LOOKUP_FAILED_MESSAGE);
        return 'unavailable';
      }
    },
    [userStore],
  );

  const onNewAccount = useCallback(
    (email: string) => {
      navigation.navigate(AppRoute.ACCOUNT_CREATE, {
        email,
        action: AccountScreenAction.CREATE,
      });
    },
    [navigation],
  );

  const onRewardsSignUp = useCallback(() => {
    // ROOT_REWARDS_FLOW is typed as taking no params, so the nested
    // {screen, params} form matches no signature. Widened once, here, rather
    // than with the `as never` pair used elsewhere, which types the call as
    // taking a single argument and so rejects the params object outright.
    const navigateToNested = navigation.navigate as (route: AppRoute, params: object) => void;

    navigateToNested(AppRoute.ROOT_REWARDS_FLOW, {
      screen: AppRoute.REWARDS_FULL_SIGN_UP,
      params: {firstName: firstName.current},
    });
  }, [navigation]);

  const onLogIn = useCallback(
    async (email: string, password: string) => {
      setPasswordErrorMessage(undefined);

      const success = await userStore.login({email, password});
      if (!success) {
        setPasswordErrorMessage(WRONG_PASSWORD_MESSAGE);
        return;
      }

      Analytics.logEvent({
        eventName: EventName.Login,
        parameters: {
          screen_name: EventScreenName.Login,
          source_type: EventSourceType.Button,
          method: 'email',
        },
      });
      userStore.afterLogin();
    },
    [userStore],
  );

  const onForgotPassword = useCallback(
    async (email: string) => {
      await userStore.initiatePasswordReset(email, () => {
        navigation.goBack();
      });

      Analytics.logEvent({
        eventName: EventName.PasswordResetRequest,
        parameters: {screen_name: EventScreenName.Login},
      });
    },
    [navigation, userStore],
  );

  const onClose = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate(AppRoute.HOME);
  }, [navigation]);

  return (
    <SignUpFlowContent
      onCheckEmail={onCheckEmail}
      onNewAccount={onNewAccount}
      onRewardsSignUp={onRewardsSignUp}
      onLogIn={onLogIn}
      onForgotPassword={onForgotPassword}
      onClose={onClose}
      passwordErrorMessage={passwordErrorMessage}
      emailErrorMessage={emailErrorMessage}
    />
  );
};

export default SignUpFlowScreen;
export {INVALID_EMAIL_MESSAGE, LOOKUP_FAILED_MESSAGE, WRONG_PASSWORD_MESSAGE};
